import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser'
import SoundAssetKeys from '../SoundAssetKeys';
import Light, { LIGHT_COLORS_ARRAY } from './Light';

const LINE_WIDTH = 1;
const LINE_COLOR = 0x000000;
const DISTANCE_BETWEEN_LIGHTS = 40;

export default class LightString extends Phaser.GameObjects.Container
{
    /**
     * 
     * @param {Phaser.Scene} scene 
     * @param {{x:number, y:number}[]} positionsArray
     */
    constructor(scene, positionsArray = [])
    {
        super(scene);

        this.scene.add.existing(this);

        /** @type {Light[]} */
        this.lights = [];
        this.currentBulbOnIndex = 0;
        this.currentLightColorIndex = 0;

        this.line = this.scene.add.graphics({ lineStyle: { color: LINE_COLOR, width: LINE_WIDTH } });
        this.add(this.line);

        let lastPt = null;
        for (let i = 0; i < positionsArray.length; i++)
        {
            let pt = positionsArray[i];

            this._addLight(pt.x, pt.y);

            if (lastPt != null)
            {
                this._connectPoints(lastPt, pt);
            }

            lastPt = pt;
        }
    }

    get lightsAdded()
    {
        return this.lights.length;
    }

    startNewString(startX, startY)
    {
        this.line = this.scene.add.graphics({ lineStyle: { color: LINE_COLOR, width: LINE_WIDTH } });
        this.add(this.line);

        ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cartoon_pop, this.scene);
        this._addLight(startX, startY);

        this.currentLinePoints = [{ x: startX, y: startY }];
        this.distance = 0;
    }

    drawLineTo(x, y)
    {
        this.currentLinePoints.push({ x: x, y: y });

        this.distance += Phaser.Math.Distance.Between(this.currentLinePoints[this.currentLinePoints.length - 2].x, this.currentLinePoints[this.currentLinePoints.length - 2].y,
            this.currentLinePoints[this.currentLinePoints.length - 1].x, this.currentLinePoints[this.currentLinePoints.length - 1].y);

        if (this.distance >= DISTANCE_BETWEEN_LIGHTS)
        {
            // draw the line segments
            for (let i = 1; i < this.currentLinePoints.length; i++)
            {
                this._connectPoints(this.currentLinePoints[i - 1], this.currentLinePoints[i]);
            }

            ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cartoon_pop, this.scene);
            this._addLight(x, y);

            this.distance = 0;
            this.currentLinePoints = [{ x: x, y: y }];
        }
    }

    /**
     * 
     * @param {{x:number, y:number}} startPt 
     * @param {{x:number, y:number}} endPt 
     */
    _connectPoints(startPt, endPt)
    {
        this.line.lineBetween(startPt.x, startPt.y, endPt.x, endPt.y);
    }

    _addLight(x, y)
    {
        let light = new Light(this.scene, x, y);
        this.add(light);
        this.lights.push(light);
    }

    /**
     * light two bulbs separated by one
     * @param {number} flashTime the number of milliseconds to show each state; default is 500 milliseconds
     * @param {number} timeToPlay if null, this sequence will play forever; otherwise it will play until the time to play is 0 (in milliseconds)
     * @returns 
     */
    async lightSequence1(flashTime = 500, timeToPlay)
    {
        // check to see if this game object is still active, if it isn't, break out of loop
        if (!this.active || timeToPlay <= 0) return;
        await ABCyaPhaser.Async.wait(flashTime, this.scene);

        timeToPlay -= flashTime;

        if (!this.active || timeToPlay <= 0) return;
        this.lights.forEach((light, index) => 
        {
            if (index % 2 == 0)
            {
                light.turnOn();
            }
            else
            {
                light.turnOff();
            }
        });

        await ABCyaPhaser.Async.wait(flashTime, this.scene);

        timeToPlay -= flashTime;

        if (!this.active || timeToPlay <= 0) return;
        this.lights.forEach((light, index) => 
        {
            if (index % 2 == 0)
            {
                light.turnOff();
            }
            else
            {
                light.turnOn();
            }
        });

        // repeat forever
        await this.lightSequence1(flashTime, timeToPlay);
    }

    /**
     * desc: light 1 bulb beginning to end
     * @param {number} onTime the number of milliseconds a light will stay on; default 80 milliseconds
     * @param {number} timeToPlay if null, this sequence will play forever; otherwise it will play until the time to play is 0 (in milliseconds)
     * @returns 
     */
    async lightSequence2(onTime = 80, timeToPlay)
    {
        if (!this.active || timeToPlay <= 0) return;
        await ABCyaPhaser.Async.wait(onTime, this.scene);

        timeToPlay -= onTime;

        if (!this.active || timeToPlay <= 0) return;

        this.lights.forEach((light, index) =>
        {
            if (index == this.currentBulbOnIndex)
            {
                light.turnOn();
            }
            else
            {
                light.turnOff();
            }
        });

        this.currentBulbOnIndex++;

        if (this.currentBulbOnIndex >= this.lights.length)
        {
            this.currentBulbOnIndex = 0;
        }

        await this.lightSequence2(onTime, timeToPlay);
    }

    /**
     * desc: light 5 bulbs in a row and advance
     * @param {number} onTime the number of milliseconds a light will stay on; default 150 milliseconds
     * @param {string} direction one of the LIGHT_SEQUENCE_DIRECTIONS constants, indicating which direction the lights will go (from start or from end)
     * @param {number} timeToPlay if null, this sequence will play forever; otherwise it will play until the time to play is 0 (in milliseconds)
     * @returns 
     */
    async lightSequence3(onTime = 150, direction = LIGHT_SEQUENCE_DIRECTIONS.forwards, timeToPlay)
    {
        if (!this.active || timeToPlay <= 0) return;
        await ABCyaPhaser.Async.wait(onTime, this.scene);

        timeToPlay -= onTime;

        if (!this.active || timeToPlay <= 0) return;
        this.lights.forEach((light, index) =>
        {
            if (index >= this.currentBulbOnIndex && index < (this.currentBulbOnIndex + 5))
            {
                light.turnOn();
            }
            else
            {
                light.turnOff();
            }
        });

        this.currentBulbOnIndex = Phaser.Math.Wrap(this.currentBulbOnIndex + (direction == LIGHT_SEQUENCE_DIRECTIONS.forwards ? 1 : -1), 0, this.lights.length);

        await this.lightSequence3(onTime, direction, timeToPlay);
    }

    /**
     * desc: turns all lights on and then off
     * @param {number} onTime the number of milliseconds a light will stay on/off; default 200 milliseconds
     * @param {number} timeToPlay if null, this sequence will play forever; otherwise it will play until the time to play is 0 (in milliseconds)
     * @returns 
     */
    async lightSequence4(onTime = 200, timeToPlay)
    {
        if (!this.active || timeToPlay <= 0) return;
        await ABCyaPhaser.Async.wait(onTime, this.scene);

        timeToPlay -= onTime;

        if (!this.active || timeToPlay <= 0) return;
        this.lights.forEach(light => light.turnOn());

        await ABCyaPhaser.Async.wait(onTime, this.scene);

        timeToPlay -= onTime;

        if (!this.active || timeToPlay <= 0) return;
        this.lights.forEach(light => light.turnOff());

        await this.lightSequence4(onTime, timeToPlay);
    }

    /**
     * desc: turns on all lights based on color
     * @param {number} onTime the number of milliseconds a light will stay on/off; default 300 milliseconds
     * @param {number} timeToPlay if null, this sequence will play forever; otherwise it will play until the time to play is 0 (in milliseconds)
     * @returns 
     */
    async lightSequence5(onTime = 300, timeToPlay)
    {
        if (!this.active || timeToPlay <= 0) return;
        await ABCyaPhaser.Async.wait(onTime, this.scene);

        timeToPlay -= onTime;

        if (!this.active || timeToPlay <= 0) return;
        this.lights.forEach(light => 
        {
            if (light.name == LIGHT_COLORS_ARRAY[this.currentLightColorIndex])
            {
                light.turnOn();
            }
            else
            {
                light.turnOff();
            }
        });

        this.currentLightColorIndex = Phaser.Math.Wrap(this.currentLightColorIndex + 1, 0, LIGHT_COLORS_ARRAY.length);

        await this.lightSequence5(onTime, timeToPlay);
    }
}

export const LIGHT_SEQUENCE_DIRECTIONS =
{
    forwards: "forwards",
    backwards: "backwards"
}